import type { TextInputProps } from './TextInput';
import { TextInput } from './TextInput';
import type { CommonInputProps } from './CommonInputProps';
import React, { useCallback, useEffect, useState } from 'react';
import { isEmptyString } from '../../util';

const CURRENT_LANGUAGE: string = window.SenseConstants?.locale ?? 'en-US';
const NUMBER_SAMPLE = 123456.789;

/**
 * Props for a {@link NumberInput} component.
 */
export interface NumberInputProps
  extends Omit<
      TextInputProps,
      | 'value'
      | 'onChange'
      | 'defaultValue'
      | 'inputMode'
      | 'type'
      | 'allowShowPassword'
      | 'multiline'
      | 'multilineRows'
    >,
    CommonInputProps<number | undefined> {
  /**
   * maximal decimal point digits allowed in the input
   */
  decimalPlaces?: number;
}

function decimalCount(num: number) {
  return num.toString().split('.')[1]?.length ?? 0;
}

const parseNumber = (
  number: string,
  places: number
): number | undefined | 'overflow' => {
  const parts = Intl.NumberFormat(CURRENT_LANGUAGE).formatToParts(
    NUMBER_SAMPLE
  );

  const groupSymbol = parts.find(el => el.type === 'group')?.value;
  const decimalSymbol = parts.find(el => el.type === 'decimal')?.value;

  // prevent usage of group symbol, since allowing it ends up being more confusing than helpful
  if (groupSymbol && number.includes(groupSymbol)) {
    return undefined;
  }

  // prevent usage of dot symbol for languages that don't use it in any way
  if (number.includes('.') && groupSymbol !== '.' && decimalSymbol !== '.') {
    return undefined;
  }

  if (decimalSymbol) {
    number = number.replace(decimalSymbol, '.');
  }

  // checks if user wants to type unwanted zeros
  if (number.includes('.') && number[number.indexOf('.') + places + 1] == '0') {
    return 'overflow';
  }

  // prevent user from typing decimal symbol when no decimals are allowed
  if (number.includes('.') && places === 0) {
    return 'overflow';
  }

  // only allow one decimal symbol
  if (number.split('.').length > 2) {
    return 'overflow';
  }

  // prevent unwanted space characters
  if (number.includes(' ')) {
    return 'overflow';
  }

  return isNaN(Number(number)) ? undefined : Number(number);
};

const formatNumber = (
  value: number | undefined | null,
  places: number
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  const parts = Intl.NumberFormat(CURRENT_LANGUAGE).formatToParts(
    NUMBER_SAMPLE
  );
  const decimalSymbol = parts.find(el => el.type === 'decimal')?.value;

  // cut off any decimal places beyond our defined `decimalPlaces` property
  const multiplier = Math.pow(10, places);
  let truncatedStringValue = (
    Math.trunc(value * multiplier) / multiplier
  ).toString();

  // return string with correct decimalSymbol
  if (decimalSymbol && decimalSymbol !== '.') {
    truncatedStringValue = truncatedStringValue.replace('.', decimalSymbol);
  }
  return truncatedStringValue;
};

/**
 * @param value max value is 9007199254740991 (MAX_SAFE_INTEGER), bigger number is cast to Scientific notation
 * @param onChange on change handler
 * @param defaultValue field default value
 * @param decimalPlaces number of decimal places that are allowed
 * @param props leftover @{link NumberInputProps}
 */
export function NumberInput({
  onChange,
  value,
  defaultValue,
  decimalPlaces = 0,
  ...props
}: Readonly<NumberInputProps>): React.JSX.Element {
  const [currIntlNumber, setCurrIntlNumber] = useState(
    formatNumber(value ?? defaultValue, decimalPlaces)
  );
  useEffect(() => {
    // Update currIntlNumber when the number value changed.
    const parsedCurrIntlNumber = parseNumber(currIntlNumber, decimalPlaces);
    const newValue = value ?? defaultValue;
    // This if check is necessary to make sure that the decimal character isn't automatically deleted when using backspace.
    if (
      typeof parsedCurrIntlNumber === 'number' &&
      parsedCurrIntlNumber !== newValue
    ) {
      setCurrIntlNumber(formatNumber(newValue, decimalPlaces));
    }
    // suppress hook since we don't want this to trigger when `currIntlNumber` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimalPlaces, defaultValue, value]);

  const changeHandlerIntl = useCallback(
    (intlNumber: string) => {
      const parsedValue = parseNumber(intlNumber, decimalPlaces);
      if (parsedValue === undefined || parsedValue === 'overflow') {
        return;
      }
      if (parsedValue && decimalCount(parsedValue) > decimalPlaces) {
        return;
      }
      setCurrIntlNumber(intlNumber);
      if (isEmptyString(intlNumber)) {
        onChange?.(undefined);
      } else {
        onChange?.(Number(parsedValue));
      }
    },
    [onChange, decimalPlaces]
  );

  return (
    <TextInput
      {...props}
      type="text"
      inputMode={decimalPlaces > 0 ? 'decimal' : 'numeric'}
      value={String(currIntlNumber)}
      onChange={changeHandlerIntl}
    />
  );
}
